import React, { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, TextField, TextArea, Select, Separator } from "@radix-ui/themes"
import {
  ArrowLeft,
  Plus,
  Minus,
  ShoppingCart,
  User,
  CreditCard,
  Receipt,
  Trash2,
  Save,
  Percent,
  Search,
  Filter,
  Tag,
  Users,
  MapPin,
  Star,
  X,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  List
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useToast } from "../../hooks/use-toast"
import { useLanguage } from "../../contexts/LanguageContext"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import TableOrdersDrawer from "../../components/TableOrdersDrawer"

interface Size {
  name: string
  nameAr?: string
  price: number
  originalPrice?: number
  isDefault: boolean
  isAvailable: boolean
}

interface FoodItem {
  _id: string
  name: string
  nameAr?: string
  description: string
  descriptionAr?: string
  price: number
  originalPrice?: number
  sizes?: Size[]
  category: string
  image: string
  isAvailable: boolean
  rating?: {
    average: number
    count: number
  }
}

interface Table {
  _id: string
  tableNumber: string
  restaurant: string
  capacity: number
  location: "indoor" | "outdoor" | "private"
  status: "available" | "occupied" | "reserved" | "maintenance"
  qrCode: {
    code: string
    url: string
    imageUrl?: string
  }
  currentOrder?: string | { _id: string; orderNumber: string; status: string }
  isActive: boolean
  notes?: string
  createdAt: string
  updatedAt: string
}

interface PaymentMethod {
  _id: string
  name: string
  code: string
  description: string
  icon: string
  isEnabled: boolean
  isDefault: boolean
  customProcessingFee: number | null
  customProcessingFeeType: 'percentage' | 'fixed'
  minAmount: number | null
  maxAmount: number | null
  processingFee: number
  processingFeeType: 'percentage' | 'fixed'
  supportedCurrencies: string[]
  configuration: any
  sortOrder: number
}

interface CartItem {
  foodItem: FoodItem
  quantity: number
  discount: number
  total: number
  selectedSize?: Size
}

// Category translation key mapping
const categoryTranslationKeys: Record<string, string> = {
  'all': 'allItems',
  'fast-food': 'fastFood',
  'Fast food': 'fastFood',
  'fastfood': 'fastFood',
  'pizza': 'pizza',
  'Pizza': 'pizza',
  'asian': 'asian',
  'Asian': 'asian',
  'middle-eastern': 'middleEastern',
  'Middle-eastern': 'middleEastern',
  'Middle Eastern': 'middleEastern',
  'desserts': 'desserts',
  'Desserts': 'desserts',
  'dessert': 'desserts',
  'healthy': 'healthy',
  'Healthy': 'healthy',
  'coffee': 'coffee',
  'Coffee': 'coffee',
  'appetizer': 'appetizer',
  'Appetizer': 'appetizer',
  'main-dish': 'mainDish',
  'Main Dish': 'mainDish',
  'side-dish': 'sideDish',
  'Side Dish': 'sideDish',
  'sandwich': 'sandwich',
  'Sandwich': 'sandwich',
  'beverage': 'beverage',
  'Beverage': 'beverage',
  'drink': 'drink',
  'Drink': 'drink',
}

const POSNewOrderPage: React.FC = () => {
  const { restaurantId } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t, language, dir } = useLanguage()
  const { user } = useRestaurantAuth()

  const [foodItems, setFoodItems] = useState<FoodItem[]>([])
  const [tables, setTables] = useState<Table[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState("")
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
    address: ""
  })

  // UI State for Collapsible Sections
  const [showCustomerInfo, setShowCustomerInfo] = useState(false)

  // Memoized derived statetOrderType] = useState("dine-in")
  const [orderType, setOrderType] = useState("dine-in")
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [paymentMethod, setPaymentMethod] = useState("")
  const [specialInstructions, setSpecialInstructions] = useState("")
  const [orderDiscount, setOrderDiscount] = useState(0)
  const [saving, setSaving] = useState(false)

  // Food item modal state
  const [selectedItem, setSelectedItem] = useState<FoodItem | null>(null)
  const [showItemModal, setShowItemModal] = useState(false)
  const [selectedSize, setSelectedSize] = useState<Size | null>(null)
  const [itemQuantity, setItemQuantity] = useState(1)

  // Table selection enhancements
  const [tableSearchTerm, setTableSearchTerm] = useState("")
  const [tableLocationFilter, setTableLocationFilter] = useState<string>("all")
  const [tableViewMode, setTableViewMode] = useState<"grid" | "list">("grid")
  const [tablePage, setTablePage] = useState(1)

  // Drawer state for occupied tables
  const [isTableDrawerOpen, setIsTableDrawerOpen] = useState(false)

  // Use Memo to get categories
  const tablesPerPage = 12

  // Tax settings
  const [taxEnabled, setTaxEnabled] = useState(false)
  const [taxPercentage, setTaxPercentage] = useState(15)

  useEffect(() => {
    fetchFoodItems()
    fetchTables()
    fetchPaymentMethods()
    fetchTaxSettings()
  }, [restaurantId])

  const fetchFoodItems = async () => {
    try {
      setLoading(true)
      const response = await restaurantPortalAPI.getRestaurantFoodItems(restaurantId!, { limit: 1000 })
      setFoodItems(response.data.foodItems.filter((item: FoodItem) => item.isAvailable))
    } catch (error) {
      console.error('Error fetching food items:', error)
      toast({
        title: t("error"),
        description: t("failedToLoadFoodItems"),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchTables = async () => {
    try {
      const response = await restaurantPortalAPI.getTables(restaurantId!)
      setTables(response.data)
    } catch (error) {
      console.error('Error fetching tables:', error)
      toast({
        title: t("error"),
        description: t("failedToLoadTables"),
        variant: "destructive",
      })
    }
  }

  const fetchPaymentMethods = async () => {
    try {
      const response = await restaurantPortalAPI.getRestaurantPaymentMethods(restaurantId!)
      setPaymentMethods(response.data.data.filter((method: PaymentMethod) => method.isEnabled))
    } catch (error) {
      console.error('Error fetching payment methods:', error)
      toast({
        title: t("error"),
        description: t("failedToLoadPaymentMethods"),
        variant: "destructive",
      })
    }
  }

  const fetchTaxSettings = async () => {
    try {
      setTaxEnabled(false)
      setTaxPercentage(15)
    } catch (error) {
      console.error("Error fetching tax settings:", error)
    }
  }

  const getCategoryLabel = (category: string): string => {
    const key = categoryTranslationKeys[category] || categoryTranslationKeys[category.toLowerCase()]
    return key ? t(key) : category
  }

  const openItemModal = (item: FoodItem) => {
    setSelectedItem(item)
    setShowItemModal(true)
    setItemQuantity(1)

    // Set default size if item has sizes
    if (item.sizes && item.sizes.length > 0) {
      const defaultSize = item.sizes.find(s => s.isDefault && s.isAvailable) ||
        item.sizes.find(s => s.isAvailable) ||
        item.sizes[0]
      setSelectedSize(defaultSize || null)
    } else {
      setSelectedSize(null)
    }
  }

  const closeItemModal = () => {
    setShowItemModal(false)
    setSelectedItem(null)
    setSelectedSize(null)
    setItemQuantity(1)
  }

  const addToCart = (foodItem: FoodItem, size?: Size, quantity: number = 1) => {
    const itemPrice = size ? size.price : foodItem.price
    const sizeKey = size ? size.name : ''

    setCart(prevCart => {
      const existingItem = prevCart.find(item =>
        item.foodItem._id === foodItem._id &&
        (item.selectedSize?.name || '') === sizeKey
      )

      if (existingItem) {
        return prevCart.map(item =>
          item.foodItem._id === foodItem._id && (item.selectedSize?.name || '') === sizeKey
            ? {
              ...item,
              quantity: item.quantity + quantity,
              total: (item.quantity + quantity) * itemPrice * (1 - item.discount / 100)
            }
            : item
        )
      } else {
        return [...prevCart, {
          foodItem,
          quantity,
          discount: 0,
          total: quantity * itemPrice,
          selectedSize: size
        }]
      }
    })

    const itemName = language === 'ar' && foodItem.nameAr ? foodItem.nameAr : foodItem.name
    const sizeName = size ? (language === 'ar' && size.nameAr ? size.nameAr : size.name) : ''

    toast({
      title: t("addedToCart"),
      description: sizeName ? `${itemName} (${sizeName}) x${quantity}` : `${itemName} x${quantity}`,
    })

    closeItemModal()
  }

  const updateQuantity = (foodItemId: string, quantity: number, sizeName?: string) => {
    if (quantity <= 0) {
      removeFromCart(foodItemId, sizeName)
      return
    }

    setCart(prevCart => prevCart.map(item => {
      if (item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || '')) {
        const price = item.selectedSize ? item.selectedSize.price : item.foodItem.price
        return { ...item, quantity, total: quantity * price * (1 - item.discount / 100) }
      }
      return item
    }))
  }

  const updateItemDiscount = (foodItemId: string, discount: number, sizeName?: string) => {
    setCart(prevCart => prevCart.map(item => {
      if (item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || '')) {
        const price = item.selectedSize ? item.selectedSize.price : item.foodItem.price
        return { ...item, discount, total: item.quantity * price * (1 - discount / 100) }
      }
      return item
    }))
  }

  const removeFromCart = (foodItemId: string, sizeName?: string) => {
    setCart(prevCart => prevCart.filter(item =>
      !(item.foodItem._id === foodItemId && (item.selectedSize?.name || '') === (sizeName || ''))
    ))
  }

  const getCartSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0)
  }

  const getCartTax = () => {
    return taxEnabled ? getCartSubtotal() * (taxPercentage / 100) : 0
  }

  const getCartTotal = () => {
    const subtotal = getCartSubtotal()
    const discount = subtotal * (orderDiscount / 100)
    const tax = getCartTax()
    return subtotal - discount + tax
  }

  const getFilteredItems = () => {
    let filtered = foodItems

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.nameAr && item.nameAr.includes(searchTerm)) ||
        (item.descriptionAr && item.descriptionAr.includes(searchTerm))
      )
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory)
    }

    return filtered
  }

  const getCategories = () => {
    const uniqueCategories = Array.from(new Set(foodItems.map(item => item.category)))
    return ['all', ...uniqueCategories]
  }

  // Enhanced table filtering
  const filteredTables = useMemo(() => {
    let filtered = tables

    if (tableSearchTerm) {
      filtered = filtered.filter(table =>
        table.tableNumber.toLowerCase().includes(tableSearchTerm.toLowerCase())
      )
    }

    if (tableLocationFilter !== 'all') {
      filtered = filtered.filter(table => table.location === tableLocationFilter)
    }

    return filtered
  }, [tables, tableSearchTerm, tableLocationFilter])

  const paginatedTables = useMemo(() => {
    const startIndex = (tablePage - 1) * tablesPerPage
    return filteredTables.slice(startIndex, startIndex + tablesPerPage)
  }, [filteredTables, tablePage])

  const totalTablePages = Math.ceil(filteredTables.length / tablesPerPage)

  const handleTableSelect = (table: Table) => {
    if (table.status === "occupied") {
      setSelectedTable(table)
      setIsTableDrawerOpen(true)
    } else {
      setSelectedTable(selectedTable?._id === table._id ? null : table)
    }
  }

  const handleCreateOrder = async () => {
    if (cart.length === 0) {
      toast({
        title: "Empty Cart",
        description: "Please add items to your order",
        variant: "destructive",
      })
      return
    }

    if (orderType === "dine-in" && !selectedTable) {
      toast({
        title: "Missing Table Selection",
        description: "Please select a table for dine-in orders",
        variant: "destructive",
      })
      return
    }

    if (orderType !== "dine-in" && !paymentMethod) {
      toast({
        title: t("missingPaymentMethod"),
        description: t("pleaseSelectPaymentMethod"),
        variant: "destructive",
      })
      return
    }

    try {
      setSaving(true)

      // Get cashier ID from authenticated user
      const cashierId = user?._id

      if (!cashierId) {
        toast({
          title: t("error"),
          description: "User authentication error. Please login again.",
          variant: "destructive",
        })
        return
      }

      const orderData = {
        restaurantId,
        cashierId,
        orderType,
        tableNumber: orderType === "dine-in" ? selectedTable?.tableNumber : null,
        tableId: orderType === "dine-in" ? selectedTable?._id : null,
        customerInfo,
        items: cart.map(item => ({
          foodItem: item.foodItem._id,
          quantity: item.quantity,
          price: item.selectedSize ? item.selectedSize.price : item.foodItem.price,
          discount: item.discount,
          total: item.total,
          size: item.selectedSize ? {
            name: item.selectedSize.name,
            nameAr: item.selectedSize.nameAr,
            price: item.selectedSize.price
          } : undefined
        })),
        pricing: {
          subtotal: getCartSubtotal(),
          tax: getCartTax(),
          discount: getCartSubtotal() * (orderDiscount / 100),
          total: getCartTotal()
        },
        paymentMethod: orderType === "dine-in" ? "cash" : paymentMethod,
        paymentStatus: orderType === "dine-in" ? "pending" : "paid",
        specialInstructions,
        status: orderType === "dine-in" ? "confirmed" : "confirmed"
      }

      await restaurantPortalAPI.createPOSOrder(orderData)

      toast({
        title: t("success"),
        description: t("posOrderCreatedSuccessfully"),
      })

      navigate(`/restaurant/${restaurantId}/orders`)
    } catch (error) {
      console.error("Error creating POS order:", error)
      toast({
        title: t("error"),
        description: t("failedToCreateOrder"),
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "SDG",
    }).format(amount)
  }

  const getLocationIcon = (location: string) => {
    switch (location) {
      case "indoor":
        return <MapPin className="h-4 w-4 text-blue-500" />
      case "outdoor":
        return <MapPin className="h-4 w-4 text-green-500" />
      case "private":
        return <MapPin className="h-4 w-4 text-purple-500" />
      default:
        return <MapPin className="h-4 w-4 text-gray-500" />
    }
  }

  const getLocationColor = (location: string) => {
    switch (location) {
      case "indoor": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
      case "outdoor": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
      case "private": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
    }
  }

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingPOSSystem")} />
  }

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("newPOSOrder")}
        subtitle={orderType === 'dine-in' && selectedTable ? `${t("table")} ${selectedTable.tableNumber}` : t("createNewPOSOrder")}
      >
        {/* Central Segmented Control for Order Type */}
        <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mx-4 flex-1 justify-center max-w-xl">
          <button
            onClick={() => { setOrderType('takeaway'); setSelectedTable(null); }}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${orderType === 'takeaway' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            {t("takeaway")}
          </button>
          <button
            onClick={() => setOrderType('dine-in')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${orderType === 'dine-in' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            {t("dineIn")}
          </button>
          <button
            onClick={() => { setOrderType('delivery'); setSelectedTable(null); }}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${orderType === 'delivery' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            {t("delivery")}
          </button>
        </div>

        <Button
          variant="outline"
          onClick={() => navigate(`/restaurant/${restaurantId}/pos`)}
          className={`${language === 'ar' ? 'ml-2' : 'mr-2'} bg-gray-200 hover:bg-gray-600 text-gray-900 dark:text-white dark:bg-gray-700 hover:text-white`}
        >
          <ArrowLeft className={`h-4 w-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
          {t("backToPOS")}
        </Button>
      </PageHeader>

      {/* Main Layout Context Switch */}
      {orderType === "dine-in" && !selectedTable ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("selectTable")}</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setTableViewMode("grid")}
                className={`p-2 rounded-lg ${tableViewMode === 'grid' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setTableViewMode("list")}
                className={`p-2 rounded-lg ${tableViewMode === 'list' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5`} />
              <input
                type="text"
                placeholder={t("searchTableNumber")}
                value={tableSearchTerm}
                onChange={(e) => { setTableSearchTerm(e.target.value); setTablePage(1) }}
                className={`w-full ${language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-gray-50`}
              />
            </div>
            <select
              value={tableLocationFilter}
              onChange={(e) => { setTableLocationFilter(e.target.value); setTablePage(1) }}
              className="px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-gray-50 min-w-[150px]"
            >
              <option value="all">{t("allLocations")}</option>
              <option value="indoor">{t("indoor")}</option>
              <option value="outdoor">{t("outdoor")}</option>
              <option value="private">{t("private")}</option>
            </select>
          </div>

          {tables.length > 0 ? (
            tableViewMode === 'grid' ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                {paginatedTables.map((table) => (
                  <button
                    key={table._id}
                    onClick={() => {
                      setSelectedTable(table)
                      setCart([])
                      if (table.status === 'occupied') {
                        setIsTableDrawerOpen(true)
                      }
                    }}
                    className={`aspect-square p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${table.status === 'occupied'
                      ? 'border-orange-500 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/40 dark:border-orange-700'
                      : 'border-gray-200 hover:border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:hover:border-gray-500'
                      }`}
                  >
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{table.tableNumber}</div>
                    <div className="flex items-center justify-center gap-1">
                      {getLocationIcon(table.location)}
                      <span className="text-xs text-gray-500 font-medium">{t(table.location)}</span>
                    </div>
                    {table.status === 'occupied' && (
                      <span className="mt-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-orange-200 text-orange-800 rounded-full dark:bg-orange-800 dark:text-orange-200">
                        {t("occupied")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedTables.map((table) => (
                  <button
                    key={table._id}
                    onClick={() => {
                      setSelectedTable(table)
                      setCart([])
                      if (table.status === 'occupied') {
                        setIsTableDrawerOpen(true)
                      }
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${table.status === 'occupied'
                      ? 'border-orange-500 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/40 dark:border-orange-700'
                      : 'border-gray-200 hover:border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:hover:border-gray-500'
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">{t("table")} {table.tableNumber}</div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getLocationColor(table.location)}`}>
                        {t(table.location)}
                      </span>
                      {table.status === 'occupied' && (
                        <span className="px-2 py-1 text-xs font-bold uppercase bg-orange-200 text-orange-800 rounded-full dark:bg-orange-800 dark:text-orange-200">
                          {t("occupied")}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {table.capacity} {t("seats")}
                    </span>
                  </button>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {t("noTablesAvailable")}
            </div>
          )}

          {totalTablePages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-500">
                {filteredTables.length} {t("tables")}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setTablePage(p => Math.max(1, p - 1))} disabled={tablePage === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium">
                  {tablePage} / {totalTablePages}
                </span>
                <button onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))} disabled={tablePage === totalTablePages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu Section */}
          <div className="lg:col-span-2">
            <Card className="h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t("menu")}
                </h2>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
                    <input
                      type="text"
                      placeholder={t("searchFoodItems")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full ${language === 'ar' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                    />
                  </div>
                </div>

                {/* Horizontal Category Tabs */}
                <div className="mb-6 overflow-x-auto scrollbar-hide">
                  <div className="flex gap-2 pb-2">
                    {getCategories().map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        {getCategoryLabel(category)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Items Count */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {getFilteredItems().length} {t("ofItems").replace("{count}", foodItems.length.toString())}
                </div>

                {/* Food Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getFilteredItems().map(item => {
                    const itemName = language === 'ar' && item.nameAr ? item.nameAr : item.name
                    const itemDesc = language === 'ar' && item.descriptionAr ? item.descriptionAr : item.description
                    const hasSizes = item.sizes && item.sizes.length > 0
                    const availableSizes = hasSizes ? item.sizes!.filter(s => s.isAvailable) : []

                    let priceDisplay = formatCurrency(item.price)
                    if (availableSizes.length > 0) {
                      const prices = availableSizes.map(s => s.price).sort((a, b) => a - b)
                      const minPrice = prices[0]
                      const maxPrice = prices[prices.length - 1]
                      priceDisplay = minPrice === maxPrice
                        ? formatCurrency(minPrice)
                        : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`
                    }

                    const cartItems = cart.filter(c => c.foodItem._id === item._id)
                    const totalInCart = cartItems.reduce((sum, c) => sum + c.quantity, 0)

                    return (
                      <div
                        key={item._id}
                        className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => openItemModal(item)}
                      >
                        <div className="relative">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={itemName}
                              className="w-full h-40 object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                              }}
                            />
                          ) : (
                            <div className="w-full h-40 bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                              <Tag className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className={`absolute top-2 ${language === 'ar' ? 'right-2' : 'left-2'}`}>
                            <span className="px-2 py-1 text-xs font-medium bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded-full">
                              {getCategoryLabel(item.category)}
                            </span>
                          </div>
                          {item.rating && item.rating.count > 0 && (
                            <div className={`absolute top-2 ${language === 'ar' ? 'left-2' : 'right-2'}`}>
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1">
                                <Star className="w-3 h-3 fill-current" />
                                {item.rating.average.toFixed(1)}
                              </span>
                            </div>
                          )}
                          {totalInCart > 0 && (
                            <div className="absolute bottom-2 right-2">
                              <span className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-full">
                                {totalInCart} {t("inCart")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">{itemName}</h3>
                            <span className="font-bold text-green-600 whitespace-nowrap text-sm">{priceDisplay}</span>
                          </div>
                          {itemDesc && (
                            <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-3">{itemDesc}</p>
                          )}
                          <div className="text-center">
                            <span className="text-xs text-red-600 font-medium">
                              {hasSizes ? t("tapToSelectSize") : t("tapToAdd")}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {getFilteredItems().length === 0 && (
                  <div className="text-center py-12">
                    <Tag className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">{t("noItemsFound")}</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Order Section */}
          <div className="space-y-6">
            {/* Order Information */}
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("orderDetails")}</h3>

                {orderType === "dine-in" && selectedTable && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-red-600" />
                        <span className="font-bold text-lg text-red-800 dark:text-red-300">
                          {t("table")} {selectedTable.tableNumber}
                        </span>
                      </div>
                      <button
                        onClick={() => { setSelectedTable(null); setCart([]) }}
                        className="text-red-600 hover:text-red-800 p-1 hover:bg-red-100 rounded-full dark:hover:bg-red-800/50"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <Button
                        variant="outline"
                        color="red"
                        onClick={() => setIsTableDrawerOpen(true)}
                      >
                        <Receipt className="w-4 h-4 mr-2" />
                        {t("viewTableOrders") || "Manage Table"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Customer Information (Collapsible) */}
                <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
                  <button
                    onClick={() => setShowCustomerInfo(!showCustomerInfo)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/80 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-gray-500" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {t("customerDetails")} <span className="text-gray-400 font-normal text-sm">({t("optional")})</span>
                      </span>
                    </div>
                    {showCustomerInfo ? <Minus className="w-4 h-4 text-gray-500" /> : <Plus className="w-4 h-4 text-gray-500" />}
                  </button>

                  {/* Customer Info Content */}
                  {showCustomerInfo && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl border-t border-gray-100 dark:border-gray-700 space-y-3">
                      <div className="flex flex-col gap-3">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">{t("customerName") || "Customer Name"}</label>
                          <TextField.Root
                            placeholder={t("enterCustomerName") || "Enter customer name"}
                            value={customerInfo.name}
                            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                            size="2"
                            className="bg-white dark:bg-gray-700"
                          >
                            <TextField.Slot>
                              <User className="w-4 h-4 text-gray-400" />
                            </TextField.Slot>
                          </TextField.Root>
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">{t("phoneNumber")}</label>
                          <TextField.Root
                            placeholder={t("enterPhoneNumber") || "Enter phone number"}
                            value={customerInfo.phone}
                            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                            size="2"
                            className="bg-white dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Cart */}
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("orderItems")}</h3>

                {cart.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {t("noItemsSelected")}
                  </p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {cart.map((item, index) => {
                      const itemName = language === 'ar' && item.foodItem.nameAr ? item.foodItem.nameAr : item.foodItem.name
                      const sizeName = item.selectedSize
                        ? (language === 'ar' && item.selectedSize.nameAr ? item.selectedSize.nameAr : item.selectedSize.name)
                        : ''
                      const itemPrice = item.selectedSize ? item.selectedSize.price : item.foodItem.price
                      const cartKey = `${item.foodItem._id}-${item.selectedSize?.name || 'default'}-${index}`

                      return (
                        <div key={cartKey} className="bg-gray-50 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 dark:text-white">{itemName}</h5>
                              {sizeName && (
                                <span className="text-xs text-red-600 font-medium">{sizeName}</span>
                              )}
                              <p className="text-sm text-gray-600 dark:text-gray-400">{formatCurrency(itemPrice)} {t("each")}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="2"
                              onClick={() => removeFromCart(item.foodItem._id, item.selectedSize?.name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Button
                                size="2"
                                variant="outline"
                                onClick={() => updateQuantity(item.foodItem._id, item.quantity - 1, item.selectedSize?.name)}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="text-sm font-medium text-gray-900 dark:text-white min-w-[2rem] text-center">
                                {item.quantity}
                              </span>
                              <Button
                                size="2"
                                variant="outline"
                                onClick={() => updateQuantity(item.foodItem._id, item.quantity + 1, item.selectedSize?.name)}
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>

                            <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'gap-2'}`}>
                              <span className="text-xs text-gray-600 dark:text-gray-400">{t("discountPercent")}:</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.discount}
                                onChange={(e) => updateItemDiscount(item.foodItem._id, parseFloat(e.target.value) || 0, item.selectedSize?.name)}
                                className="w-14 h-6 text-xs px-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                              />
                            </div>
                          </div>

                          <div className={`${language === 'ar' ? 'text-left' : 'text-right'} mt-2`}>
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(item.total)}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </Card>

            {/* Order Summary */}
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("orderSummary")}</h3>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t("subtotal")}:</span>
                    <span className="font-medium">{formatCurrency(getCartSubtotal())}</span>
                  </div>

                  {/* Order Discount */}
                  <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'gap-2'}`}>
                    <label className="text-sm text-gray-600 dark:text-gray-400">{t("orderDiscount")} %:</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={orderDiscount}
                      onChange={(e) => setOrderDiscount(parseFloat(e.target.value) || 0)}
                      className="w-20 h-8 text-sm px-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {orderDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t("discount")}:</span>
                      <span>-{formatCurrency(getCartSubtotal() * (orderDiscount / 100))}</span>
                    </div>
                  )}

                  {/* Tax */}
                  {taxEnabled && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t("tax")} ({taxPercentage}%):</span>
                      <span className="font-medium">{formatCurrency(getCartTax())}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-lg">{t("total")}:</span>
                      <span className="font-semibold text-lg">{formatCurrency(getCartTotal())}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Method - Only required when not Dine-in */}
                {orderType !== "dine-in" && (
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">{t("paymentMethod")}</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">{t("selectPaymentMethod")}</option>
                      {paymentMethods.map((method) => (
                        <option key={method._id} value={method._id}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Special Instructions */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">{t("specialInstructions")}</label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder={t("anySpecialInstructions")}
                    className="w-full mt-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={2}
                  />
                </div>
              </div>

              {/* Action Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <Button
                  onClick={handleCreateOrder}
                  disabled={saving || cart.length === 0}
                  className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 text-white font-bold"
                  size="4"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t("creatingOrder")}
                    </>
                  ) : (
                    <>
                      {orderType === 'dine-in' ? (
                        <><ShoppingCart className="h-5 w-5 mr-2" /> {t("sendToKitchen") || "Send to Kitchen"}</>
                      ) : (
                        <><Receipt className="h-5 w-5 mr-2" /> {t("payAndComplete") || "Pay & Complete Order"}</>
                      )}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div >
      )}

      {/* Table Orders Drawer */}
      {
        selectedTable && (
          <TableOrdersDrawer
            isOpen={isTableDrawerOpen}
            onClose={() => setIsTableDrawerOpen(false)}
            tableId={selectedTable._id}
            tableNumber={selectedTable.tableNumber}
            restaurantId={restaurantId || ''}
            onTableClosed={() => {
              setSelectedTable(null)
              fetchTables()
            }}
            onAddItems={() => {
              setIsTableDrawerOpen(false)
            }}
          />
        )
      }

      {/* Food Item Detail Modal */}
      {
        showItemModal && selectedItem && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={closeItemModal}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
                {/* Modal Header with Image */}
                <div className="relative">
                  {selectedItem.image ? (
                    <img
                      src={selectedItem.image}
                      alt={language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <Tag className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <button
                    onClick={closeItemModal}
                    className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {selectedItem.rating && selectedItem.rating.count > 0 && (
                    <div className="absolute bottom-3 left-3">
                      <span className="px-3 py-1 text-sm font-medium bg-yellow-400 text-yellow-900 rounded-full flex items-center gap-1">
                        <Star className="w-4 h-4 fill-current" />
                        {selectedItem.rating.average.toFixed(1)} ({selectedItem.rating.count})
                      </span>
                    </div>
                  )}
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-12rem)]">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {language === 'ar' && selectedItem.nameAr ? selectedItem.nameAr : selectedItem.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {language === 'ar' && selectedItem.descriptionAr ? selectedItem.descriptionAr : selectedItem.description}
                  </p>

                  {/* Size Selection */}
                  {selectedItem.sizes && selectedItem.sizes.length > 0 ? (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t("selectSize")}</h3>
                      <div className="space-y-2">
                        {selectedItem.sizes.filter(s => s.isAvailable).map((size) => {
                          const sizeName = language === 'ar' && size.nameAr ? size.nameAr : size.name
                          return (
                            <button
                              key={size.name}
                              onClick={() => setSelectedSize(size)}
                              className={`w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between ${selectedSize?.name === size.name
                                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                }`}
                            >
                              <span className="font-medium text-gray-900 dark:text-white">{sizeName}</span>
                              <span className="font-bold text-green-600">{formatCurrency(size.price)}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedItem.price)}</div>
                    </div>
                  )}

                  {/* Quantity Selector */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t("quantity")}</h3>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setItemQuantity(q => Math.max(1, q - 1))}
                        className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                      <span className="text-xl font-bold text-gray-900 dark:text-white min-w-[3rem] text-center">
                        {itemQuantity}
                      </span>
                      <button
                        onClick={() => setItemQuantity(q => q + 1)}
                        className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <Button
                    onClick={() => addToCart(selectedItem, selectedSize || undefined, itemQuantity)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold"
                    disabled={selectedItem.sizes && selectedItem.sizes.length > 0 && !selectedSize}
                  >
                    <ShoppingCart className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {t("addToOrder")} - {formatCurrency(
                      (selectedSize ? selectedSize.price : selectedItem.price) * itemQuantity
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )
      }
    </div >
  )
}

export default POSNewOrderPage